import { AutocompleteGenericEntity, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { updateAssignMultipleList, updateList } from '@/apis/lists';
import { isTemplatePlaceholder } from '@/types';
import { ListSummary } from '@/types/shared';
import { logger } from '@/util';
import AgLoadingButton from '@components/AgLoadingButton';
import ButtonRow from '@components/ButtonRow';
import DrawerAccordion, { AccordionField } from '@components/DrawerAccordion';
import UsersFetchAutocomplete from '@components/UsersFetchAutocomplete';
import { useUserDetails } from '@hooks/useUserDetails';

export type AssignListPanelContentProps = {
  handleCancel?: () => void;
  handleAssign?: (destinationTab: string) => void;
  list: Pick<ListSummary, 'assignees' | 'coordinators' | 'id' | 'count' | 'numAssigned'>;
};

function areSetsEqual(setA: Set<string>, setB: Set<string>) {
  return setA.size === setB.size && [...setA].every((value) => setB.has(value));
}

const useHandleAssignAllToMe = (
  listId: number,
  handleAssign: undefined | ((destination: string) => void)
) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently, user } = useAuth0();
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();

  return useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      const assignedTo = user?.sub ?? '';
      logger.debug('mutating');
      return updateList(token, listId, { assignedTo });
    },
    onError: () => enqueueErrorNotification('Contacts could not be assigned.'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
      enqueueSuccessNotification('All contacts have been assigned to you.');
      if (handleAssign) handleAssign('detail');
    },
  });
};

const useHandleAssignMultiple = (
  list: Pick<ListSummary, 'assignees' | 'id'>,
  numAdditionalContacts: number,
  handleAssign: undefined | ((destination: string) => void)
) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently, user } = useAuth0();
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const { data: userDetails } = useUserDetails();

  return useMutation({
    mutationFn: async ({
      updatedAssignees,
      updatedCoordinators,
    }: {
      updatedAssignees: AutocompleteGenericEntity[];
      updatedCoordinators: AutocompleteGenericEntity[];
    }) => {
      const token = await getAccessTokenSilently();
      return updateAssignMultipleList(token, list.id, {
        assignedToSubs: updatedAssignees.map(({ id }) => id),
        coordinatorSubs: updatedCoordinators.map(({ id }) => id),
        numAdditionalContacts,
      });
    },
    onError: () => enqueueErrorNotification('Contacts could not be assigned.'),
    onSuccess: (_, { updatedAssignees, updatedCoordinators }) => {
      enqueueSuccessNotification('Contacts have been assigned');
      queryClient.invalidateQueries({ queryKey: ['list', list.id] });
      if (handleAssign) {
        const userSub = user?.sub ?? '';
        const willUserBeACoordinator =
          userDetails?.userLevel === 'Principal Coordinator' ||
          updatedCoordinators.some(({ id }) => id === userSub);
        const willUserBeACaller =
          updatedAssignees.some(({ id }) => id === userSub) ||
          (updatedAssignees.length === 0 &&
            list.assignees.find((assignee) => assignee.id === userSub));

        if (willUserBeACoordinator) {
          handleAssign('dashboard');
        } else if (willUserBeACaller) {
          handleAssign('detail');
        } else {
          handleAssign('lists');
        }
      }
    },
  });
};

const AssignListPanelContent = (props: AssignListPanelContentProps) => {
  const { handleCancel, handleAssign, list } = props;

  const currentCoordinators = list.coordinators.map((coordinator) => ({
    id: coordinator.id,
    label: coordinator.name,
  }));

  const [coordAddEntities, setCoordAddEntities] =
    useState<AutocompleteGenericEntity[]>(currentCoordinators);
  const [toIndividualsAccord, setToIndividualsAccord] = useState(true);
  const [manageCoordinatorsAccord, setManageCoordinatorsAccord] = useState(false);
  const [userAddEntities, setUserAddEntities] = useState<AutocompleteGenericEntity[]>([]);
  const [numAdditionalContacts, setNumAdditionalContacts] = useState(0);

  const coordinatorsTheSame = areSetsEqual(
    new Set(coordAddEntities.map(({ id }) => String(id))),
    new Set(list.coordinators.map(({ id }) => id))
  );

  const canAddNewCoordinators = coordAddEntities.length > 0 && !coordinatorsTheSame;
  const redundantNumAddContacts = numAdditionalContacts > 0 && userAddEntities.length === 0;

  const handleChangeNumAdditionalContacts = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    setNumAdditionalContacts(isNaN(value) ? 0 : value);
  };

  const { mutate: handleAssignAllToMe, isPending: isAssignAllToMeLoading } = useHandleAssignAllToMe(
    list.id,
    handleAssign
  );

  const { mutate: handleAssignMultiple, isPending: isAssignMultipleLoading } =
    useHandleAssignMultiple(list, numAdditionalContacts, handleAssign);

  const isLoading = isAssignAllToMeLoading || isAssignMultipleLoading;
  const writesBlocked = useRedisWritesBlocked();

  const noUnassigned = list.count - list.numAssigned;
  const entityCount = userAddEntities.length + numAdditionalContacts;
  const splitNumber =
    entityCount >= noUnassigned
      ? 1
      : entityCount === 0
        ? 0
        : Math.floor(noUnassigned / entityCount);

  const allAssigned = list.numAssigned === list.count;
  const textColor = allAssigned ? 'gray' : 'rgba(0, 0, 0, 0.87)';

  return (
    <>
      <Box sx={{
        borderBottom: "1px solid #0000001f"
      }}>
        <div
          style={{
            backgroundColor: 'white',
            color: textColor,
            paddingLeft: '16px',
            paddingRight: '16px',
          }}
        >
          <>
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Typography component="h3" variant="h6" sx={{
                color: "inherit"
              }}>
                Assign callers
              </Typography>

              <Typography variant="explainer" data-testid="assignment-explainer" sx={{
                display: "block"
              }}>
                <span>
                  There are <strong>{noUnassigned} unassigned contacts</strong> in your list. Choose
                  the users you would like to assign these remaining contacts to.
                </span>
              </Typography>
            </div>
          </>
        </div>
        <DrawerAccordion
          headerText="To individuals"
          expanded={allAssigned ? false : toIndividualsAccord}
          onChange={(_event, newValue) => {
            setToIndividualsAccord(newValue);
          }}
          disabled={allAssigned}
        >
          <AccordionField
            explainer
            title="The list will automatically be evenly split between users"
          />
          <AccordionField>
            <UsersFetchAutocomplete
              label={'Search for users'}
              value={userAddEntities}
              onChange={(value) => {
                setUserAddEntities(isTemplatePlaceholder(value) ? [] : value);
              }}
              dataTestidPrefix={'assignedUsers'}
            />
            <Typography variant="explainer" component="p">
              To keep some contacts to assign later, indicate the likely number of additional
              callers.
            </Typography>

            <TextField
              id="num-additional-contacts"
              label="Additional number of users"
              variant="filled"
              slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]+' } }}
              value={numAdditionalContacts === 0 ? '' : numAdditionalContacts}
              onChange={handleChangeNumAdditionalContacts}
              sx={{ width: '50%', marginTop: '16px' }}
            />

            {splitNumber !== 0 && (
              <Typography variant="explainer" component="p">
                Each user will be assigned approx. {splitNumber} contacts
              </Typography>
            )}
          </AccordionField>
        </DrawerAccordion>
        <DrawerAccordion
          headerText="To me"
          expanded={allAssigned ? false : !toIndividualsAccord}
          onChange={(_event, newValue) => {
            setToIndividualsAccord(!newValue);
          }}
          disabled={allAssigned}
        >
          <AccordionField explainer title="Use this if you will be the only caller" />
          <AccordionField>
            <AgLoadingButton
              variant="outlined"
              disabled={writesBlocked || list.count === list.numAssigned}
              loading={isLoading}
              onClick={() => {
                if (writesBlocked) return;
                handleAssignAllToMe();
              }}
              sx={{ justifySelf: 'end' }}
            >
              Assign all to me
            </AgLoadingButton>
          </AccordionField>
        </DrawerAccordion>
        <DrawerAccordion
          largeTitle
          headerText="Manage coordinators"
          expanded={manageCoordinatorsAccord}
          onChange={(_event, newValue) => {
            setManageCoordinatorsAccord(newValue);
          }}
        >
          <AccordionField>
            <UsersFetchAutocomplete
              label={'Search for users'}
              value={coordAddEntities}
              onChange={(value) => {
                setCoordAddEntities(isTemplatePlaceholder(value) ? [] : value);
              }}
              dataTestidPrefix={'coordinatorUsers'}
              error={coordAddEntities.length === 0}
              helperText={
                coordAddEntities.length === 0
                  ? 'You must have at least one coordinator assigned to the list'
                  : ''
              }
            />
          </AccordionField>
        </DrawerAccordion>
      </Box>

      <ButtonRow>
        {handleCancel && (
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isLoading}
            data-testid="assignListPanel-cancel"
          >
            Cancel
          </Button>
        )}

        <AgLoadingButton
          variant="contained"
          disabled={
            writesBlocked ||
            redundantNumAddContacts ||
            coordAddEntities.length === 0 ||
            !(canAddNewCoordinators || userAddEntities.length > 0)
          }
          loading={isLoading}
          data-testid="assignListPanel-assign"
          onClick={() => {
            if (writesBlocked) return;
            handleAssignMultiple({
              updatedCoordinators: coordAddEntities,
              updatedAssignees: userAddEntities,
            });
          }}
        >
          Assign
        </AgLoadingButton>
      </ButtonRow>
    </>
  );
};

export default AssignListPanelContent;
